import { ref, computed, watch, toValue } from "vue";
import { usePersistentStore } from "@/store";

// Single persistent-store leaf holding all source result tabs, keyed by `${sourceIndex}/${mode}`.
const STORE_PATH = "app/states/sourceResultTabs";

function generateName(existingNames) {
  let i = 1;
  while (existingNames.includes(`result${i}`)) i++;
  return `result${i}`;
}

// IndexedDB cache for tab rows/headers
const IDB_NAME = "datalake-result-cache";
const IDB_STORE = "tabData";
const IDB_VERSION = 1;

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onupgradeneeded = (e) => e.target.result.createObjectStore(IDB_STORE);
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror = (e) => {
        dbPromise = null;
        reject(e.target.error);
      };
    });
  }
  return dbPromise;
}

async function idbGet(id) {
  try {
    const db = await getDB();
    return await new Promise((resolve) => {
      const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).get(id);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(id, data) {
  try {
    const db = await getDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(data, id);
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
  } catch {}
}

async function idbDelete(id) {
  try {
    const db = await getDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
  } catch {}
}

// Clear the entire tab-data object store (all sources/modes). Used on logout so the
// previous user's cached search result rows don't survive on a shared workstation.
export async function clearAll() {
  try {
    const db = await getDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).clear();
      tx.oncomplete = resolve;
      tx.onerror = reject;
    });
  } catch {}
}

export function useResultTabs(storageKey) {
  const store = usePersistentStore();
  const resolvedKey = computed(() => toValue(storageKey));

  // Metadata persisted via the app's persistent store. All sources/modes share one
  // `app/states/sourceResultTabs` map; this composable reads/writes the slice for the active key.
  const persistedMeta = computed({
    get: () => store.getValue(STORE_PATH)?.[resolvedKey.value] ?? [],
    set: (val) => {
      const map = { ...(store.getValue(STORE_PATH) ?? {}) };
      map[resolvedKey.value] = val;
      store.setValue(STORE_PATH, map);
    },
  });

  // Runtime: rows per tab id (not persisted to localStorage)
  const tabData = ref({});

  // Clear runtime row cache when the source/mode switches (metadata follows the key automatically)
  watch(resolvedKey, () => {
    tabData.value = {};
  });

  const resultTabs = computed(() =>
    persistedMeta.value.map((meta) => ({
      ...meta,
      headers: tabData.value[meta.id]?.headers || {},
      rows: tabData.value[meta.id]?.rows || [],
      loading: tabData.value[meta.id]?.loading || false,
    })),
  );

  const addResult = ({ relation, mode, data, query = "", operator = "like" }) => {
    const id = `r_${Date.now()}`;
    const name = generateName(persistedMeta.value.map((t) => t.name));
    persistedMeta.value = [...persistedMeta.value, { id, name, relation, mode, query, operator }];
    const entry = { headers: data.headers || {}, rows: data.rows || [] };
    tabData.value = { ...tabData.value, [id]: { ...entry, loading: false } };
    idbSet(id, entry);
    return id;
  };

  const deleteResult = (id) => {
    persistedMeta.value = persistedMeta.value.filter((t) => t.id !== id);
    const next = { ...tabData.value };
    delete next[id];
    tabData.value = next;
    idbDelete(id);
  };

  const renameResult = (id, newName) => {
    persistedMeta.value = persistedMeta.value.map((t) => (t.id === id ? { ...t, name: newName } : t));
  };

  const updateResultRelation = (id, relation) => {
    persistedMeta.value = persistedMeta.value.map((t) => (t.id === id ? { ...t, relation } : t));
  };

  const setResultLoading = (id, loading) => {
    tabData.value = { ...tabData.value, [id]: { ...(tabData.value[id] || {}), loading } };
  };

  const setResultData = (id, { headers, rows }) => {
    tabData.value = { ...tabData.value, [id]: { headers, rows, loading: false } };
    idbSet(id, { headers, rows });
  };

  // Load cached rows from IDB for all persisted tabs.
  // Returns a Set of ids that were successfully loaded from cache.
  const initFromCache = async () => {
    const ids = persistedMeta.value.map((m) => m.id);
    const entries = await Promise.all(ids.map((id) => idbGet(id).then((data) => ({ id, data }))));
    const loaded = new Set();
    const patch = {};
    for (const { id, data } of entries) {
      if (data) {
        patch[id] = { headers: data.headers || {}, rows: data.rows || [], loading: false };
        loaded.add(id);
      }
    }
    if (Object.keys(patch).length) {
      tabData.value = { ...tabData.value, ...patch };
    }
    return loaded;
  };

  return {
    resultTabs,
    persistedMeta,
    addResult,
    deleteResult,
    renameResult,
    updateResultRelation,
    setResultLoading,
    setResultData,
    initFromCache,
  };
}
